// src/pages/EmployerProfile.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {} from "firebase/storage";
import { FaUserTie } from "react-icons/fa";
import { IoChevronBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { parsePlace } from "../utils/parsePlace";
import "../styles/EmployerProfile.css";
import { getEmployees } from "../firebase/employees";
import { isPilotActive } from "../utils/pilot";

/* ---------- helpers ---------- */

const composeDisplay = (a) =>
  [a.city, a.stateProvince, a.country].filter(Boolean).join(", ");

// If user typed a full address but didn’t pick a suggestion,
// try to geocode to normalize and get lat/lng.
async function geocodeFallback({
  street,
  city,
  stateProvince,
  postalCode,
  country,
}) {
  const q = [street, city, stateProvince, postalCode, country]
    .filter(Boolean)
    .join(", ");
  if (!q) return null;

  try {
    const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
    url.searchParams.set("address", q);
    url.searchParams.set(
      "key",
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
    );
    const res = await fetch(url.toString());
    const data = await res.json();
    if (data.status !== "OK" || !data.results?.length) return null;

    const r = data.results[0];
    const comps = r.address_components || [];
    const get = (...types) =>
      comps.find((c) => c.types?.some((t) => types.includes(t)));

    const number = get("street_number")?.long_name || "";
    const route = get("route")?.long_name || "";
    const line1 = number && route ? `${number} ${route}` : route || street;

    return {
      street: line1,
      city:
        get("locality")?.long_name ||
        get("sublocality")?.long_name ||
        get("administrative_area_level_2")?.long_name ||
        city,
      stateProvince:
        get("administrative_area_level_1")?.short_name ||
        get("administrative_area_level_1")?.long_name ||
        stateProvince,
      postalCode: get("postal_code")?.long_name || postalCode,
      country: get("country")?.long_name || country,
      geo: {
        lat: r.geometry?.location?.lat ?? null,
        lng: r.geometry?.location?.lng ?? null,
      },
    };
  } catch {
    return null;
  }
}

/* ---------- component ---------- */

export default function EmployerProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [employeeUsage, setEmployeeUsage] = useState({
    used: 0,
    limit: null, // null = unknown
  });

  // const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    companyName: "",
    industry: "",
  });

  // structured company address (source of truth)
  const [address, setAddress] = useState({
    street: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    geo: null, // {lat, lng} | null
  });

  const displayLocation = useMemo(() => composeDisplay(address), [address]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (!userSnap.exists()) throw new Error("User not found");
        const userData = userSnap.data();

        const companySnap = await getDoc(
          doc(db, "companies", userData.companyId)
        );
        if (!companySnap.exists()) throw new Error("Company not found");
        const companyData = companySnap.data();
        try {
          const companyId = userData.companyId;

          // ✅ 1) count employees
          const employeesList = await getEmployees(companyId); // uses your existing helper
          const used = employeesList.length;

          // ✅ 2) plan/pilot effective limit (same logic you used in ManageEmployees)
          const planLimitRaw = Number(companyData.employeeLimit);
          const planLimit = Number.isFinite(planLimitRaw) ? planLimitRaw : null;

          const pilotObj = companyData.pilot || null;
          const pilotLimitRaw = Number(pilotObj?.employeeLimit);
          const pilotLimit = Number.isFinite(pilotLimitRaw)
            ? pilotLimitRaw
            : null;

          const pilotActive = isPilotActive(pilotObj);
          const effective = pilotActive && pilotLimit ? pilotLimit : planLimit;

          setEmployeeUsage({ used, limit: effective ?? null });
        } catch (e) {
          console.warn("Failed to load employee usage:", e);
        }

        setUserInfo(userData);
        setCompanyInfo(companyData);

        setFormData({
          name: userData.name || "",
          phone: userData.phone || "",
          companyName: companyData.name || "",
          industry: companyData.industry || "",
        });

        const addr = companyData.address || {};
        setAddress({
          street: addr.street || "",
          city: addr.city || "",
          stateProvince: addr.stateProvince || "",
          postalCode: addr.postalCode || "",
          country: addr.country || "",
          geo: addr.geo && addr.geo.lat != null ? addr.geo : null,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  const handleSave = async () => {
    if (!userInfo?.companyId) return;

    try {
      // If the admin typed but didn’t choose a suggestion, try to normalize.
      let normalized = {};
      if (!address.geo || address.geo.lat == null) {
        const fallback = await geocodeFallback(address);
        if (fallback) normalized = fallback;
      }

      const nextAddress = {
        street: normalized.street ?? address.street ?? "",
        city: normalized.city ?? address.city ?? "",
        stateProvince: normalized.stateProvince ?? address.stateProvince ?? "",
        postalCode: normalized.postalCode ?? address.postalCode ?? "",
        country: normalized.country ?? address.country ?? "",
        geo: normalized.geo ?? address.geo ?? null,
      };

      // 1) update user basics
      await updateDoc(doc(db, "users", user.uid), {
        name: formData.name,
        phone: formData.phone,
      });

      // 2) update company profile
      const updatedCompany = {
        name: formData.companyName,
        industry: formData.industry,
        address: nextAddress, // single source of truth
      };
      await updateDoc(doc(db, "companies", userInfo.companyId), updatedCompany);

      // reflect state without reload
      setCompanyInfo((prev) => ({ ...(prev || {}), ...updatedCompany }));
      setAddress(nextAddress);
      setEditMode(false);
    } catch (err) {
      console.error("Error updating profile:", err);
    }
  };

  if (loading) {
    return (
      <div className="ep-shell">
        <div className="ep-header skeleton" />
        <div className="ep-grid skeleton" />
      </div>
    );
  }

  return (
    <div className="ep-wrap">
      {/* Top breadcrumb */}
      <button className="ep-back" onClick={() => navigate("/admin-dashboard")}>
        <IoChevronBack /> <span>Dashboard</span>
      </button>

      {/* Header card */}
      <div className="ep-card ep-header">
        <div className="ep-avatar">
          {companyInfo?.logoURL ? (
            <img src={companyInfo.logoURL} alt="Company logo" />
          ) : (
            <FaUserTie />
          )}
        </div>

        <div className="ep-head-main">
          <h1>{formData.companyName || "Company"}</h1>
          <p className="ep-sub">{userInfo?.email}</p>
        </div>

        <div className="ep-head-actions">
          {!editMode ? (
            <button className="btn-primary" onClick={() => setEditMode(true)}>
              Edit
            </button>
          ) : (
            <div className="ep-sticky-actions">
              <button className="btn-primary" onClick={handleSave}>
                Save
              </button>
              <button className="btn-ghost" onClick={() => setEditMode(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Details grid */}
      <div className="ep-grid">
        <section className="ep-card">
          <h2 className="ep-section-title">Owner</h2>
          <div className="ep-field">
            <label>Full Name</label>
            <input
              value={formData.name}
              disabled={!editMode}
              onChange={(e) =>
                setFormData((s) => ({ ...s, name: e.target.value }))
              }
              className={editMode ? "ep-input-edit" : ""}
            />
          </div>

          <div className="ep-field">
            <label>Phone</label>
            <input
              value={formData.phone}
              disabled={!editMode}
              onChange={(e) =>
                setFormData((s) => ({ ...s, phone: e.target.value }))
              }
              className={editMode ? "ep-input-edit" : ""}
              placeholder="(555) 555-5555"
            />
          </div>

          <div className="ep-field">
            <label>Role</label>
            <input value={userInfo?.role || "—"} disabled />
          </div>
        </section>

        <section className="ep-card">
          <h2 className="ep-section-title">Company</h2>
          <div className="ep-field">
            <label>Company Name</label>
            <input
              value={formData.companyName}
              disabled={!editMode}
              onChange={(e) =>
                setFormData((s) => ({ ...s, companyName: e.target.value }))
              }
              className={editMode ? "ep-input-edit" : ""}
            />
          </div>

          <div className="ep-field">
            <label>Industry</label>
            <input
              value={formData.industry}
              disabled={!editMode}
              onChange={(e) =>
                setFormData((s) => ({ ...s, industry: e.target.value }))
              }
              className={editMode ? "ep-input-edit" : ""}
            />
          </div>

          <div className="ep-field">
            <label>Company Size</label>
            <input
              disabled
              value={
                companyInfo?.size ? `${companyInfo.teamSize} employees` : "—"
              }
            />
          </div>

          <div className="ep-field">
            <label>Employees Limit Used</label>
            <input
              disabled
              value={`${employeeUsage.used}/${employeeUsage.limit ??
                "∞"} employees`}
            />
          </div>

          <div className="ep-field">
            <label>Created At</label>
            <input
              disabled
              value={
                companyInfo?.createdAt?.toDate
                  ? companyInfo.createdAt.toDate().toLocaleDateString("en-US")
                  : "—"
              }
            />
          </div>
        </section>

        <section className="ep-card">
          <h2 className="ep-section-title">Location</h2>

          <div className="ep-field">
            <label>Display Location</label>
            <input disabled value={displayLocation || "—"} />
          </div>

          {editMode && (
            <>
              {/* Address picker (street/line1) */}
              <div className="ep-field">
                <label>Street</label>
                <AddressAutocomplete
                  value={address.street}
                  onChange={(val) => setAddress((s) => ({ ...s, street: val }))}
                  onSelect={(place) => {
                    if (!place) return;
                    const p = parsePlace(place); // {street, city, stateProvince, postalCode, country, lat, lng}
                    setAddress((s) => ({
                      ...s,
                      street: p.street || "",
                      city: p.city || "",
                      stateProvince: p.stateProvince || "",
                      postalCode: p.postalCode || "",
                      country: p.country || "",
                      geo:
                        p.lat != null && p.lng != null
                          ? { lat: p.lat, lng: p.lng }
                          : s.geo,
                    }));
                  }}
                  // requestOptions={{ componentRestrictions: { country: "us" } }}
                  placeholder="Start typing your address…"
                />
              </div>

              <div className="ep-field">
                <label>City</label>
                <input
                  value={address.city}
                  onChange={(e) =>
                    setAddress((s) => ({ ...s, city: e.target.value }))
                  }
                  className="ep-input-edit"
                />
              </div>
              <div className="ep-field">
                <label>State/Province</label>
                <input
                  value={address.stateProvince}
                  onChange={(e) =>
                    setAddress((s) => ({ ...s, stateProvince: e.target.value }))
                  }
                  className="ep-input-edit"
                />
              </div>
              <div className="ep-field">
                <label>Postal Code</label>
                <input
                  value={address.postalCode}
                  onChange={(e) =>
                    setAddress((s) => ({ ...s, postalCode: e.target.value }))
                  }
                  className="ep-input-edit"
                />
              </div>
              <div className="ep-field">
                <label>Country</label>
                <input
                  value={address.country}
                  onChange={(e) =>
                    setAddress((s) => ({ ...s, country: e.target.value }))
                  }
                  className="ep-input-edit"
                />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
